import React from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {isEmpty} from 'lodash/lang';
import moment from 'moment';
import {Paper, Table, TableHead, TableRow, TableCell, TableBody, Tooltip} from '@material-ui/core';
import {RemoveRedEye as RemoveRedEyeIcon} from '@material-ui/icons';
import {withKluwellTableWrapper, KluwellTableCore, SortingManager, VisiblePassword} from 'components/common';
import {buildUserFullName} from 'utils/common';

export const countWeeklyReadings = (recentReadings) => {
  let cnt = 0;
  const startDatetime = moment().startOf('week');
  const endDatetime = moment().endOf('week');
  const countedDate = new Set();
  for (let {dateCreated} of recentReadings) {
    dateCreated = moment(dateCreated).local();
    const dateCreatedStr = dateCreated.format('YYYY-MM-DD');
    if (countedDate.has(dateCreatedStr)) continue;
    countedDate.add(dateCreatedStr);
    dateCreated = moment(dateCreated);
    if (dateCreated.isSameOrAfter(startDatetime) && dateCreated.isSameOrBefore(endDatetime)) {
      ++cnt;
    }
  }
  return cnt;
};

const countRecentReadings = (recentReadings) => {
  const cntArray = [0, 0, 0, 0, 0, 0, 0];
  const weekRanges = [0, 1, 2, 3, 4, 5, 6].map((idx) => ({
    startDatetime: moment()
      .subtract(6 - idx, 'weeks')
      .startOf('week'),
    endDatetime: moment()
      .subtract(6 - idx, 'weeks')
      .endOf('week'),
  }));
  const countedDate = new Set();
  for (let {dateCreated} of recentReadings) {
    dateCreated = moment(dateCreated).local();
    const dateCreatedStr = dateCreated.format('YYYY-MM-DD');
    if (countedDate.has(dateCreatedStr)) continue;
    countedDate.add(dateCreatedStr);
    const idx = weekRanges.findIndex(
      ({startDatetime, endDatetime}) =>
        dateCreated.isSameOrAfter(startDatetime) && dateCreated.isSameOrBefore(endDatetime),
    );
    if (idx !== -1) {
      ++cntArray[idx];
    }
  }
  return cntArray;
};

const buildClassroomReaderRecords = (readers) => {
  const sortedIds = readers.sortedIds || [];
  return isEmpty(sortedIds)
    ? []
    : sortedIds.map((readerId) => {
        const reader = readers[readerId];
        return {
          record: reader,
          id: readerId,
          name: buildUserFullName(reader),
          username: reader.username,
          rawPassword: reader.rawPassword,
          yearlyReadings: reader.yearlyReadings,
          weeklyReadings: countWeeklyReadings(reader.recentReadings),
          recentReadings: countRecentReadings(reader.recentReadings),
          actions: '',
        };
      });
};

const buildClassroomReaderSettings = () => ({
  name: {
    fieldText: 'Name',
    sortable: true,
  },
  username: {
    fieldText: 'Username',
  },
  rawPassword: {
    fieldText: 'Password',
    formatData: (cell) => <VisiblePassword password={cell} />,
  },
  yearlyReadings: {
    fieldText: 'YTD Readings',
  },
  weeklyReadings: {
    fieldText: 'Weekly Readings',
  },
  recentReadings: {
    fieldText: 'Recent Readings',
    formatData: (cell) => (
      <Tooltip
        placement="right-end"
        title={
          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date Range</TableCell>
                  <TableCell>Readings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cell.map((readings, idx) => {
                  const weekStart = moment()
                    .startOf('week')
                    .add(1, 'day')
                    .subtract(6 - parseInt(idx), 'weeks');
                  const weekEnd = weekStart
                    .clone()
                    .endOf('week')
                    .add(1, 'day');
                  return (
                    <TableRow key={idx}>
                      <TableCell>{`${weekStart.format('DD/MM/YY')}  -  ${weekEnd.format('DD/MM/YY')}`}</TableCell>
                      <TableCell>{readings}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        }
      >
        <div className="recent-readings-chart">
          {cell.map((readings, idx) => {
            const height = Math.min((readings / 9) * 100, 100);
            return (
              <Tooltip key={idx} title={readings} placement="top">
                <div className="day-reading">
                  <div className="reading-left" style={{height: `${100 - height}%`}} />
                  <div className="reading-count" style={{height: `${height}%`}} />
                </div>
              </Tooltip>
            );
          })}
        </div>
      </Tooltip>
    ),
  },
  actions: {
    fieldText: 'Actions',
    formatData: (cell, row) => (
      <Link to={`/users/${row.id}`} title="view">
        <RemoveRedEyeIcon />
      </Link>
    ),
  },
});

class ClassroomReaderTableCore extends React.Component {
  static propTypes = {
    recordPages: PropTypes.object.isRequired,
    onSortableFieldClick: PropTypes.func.isRequired,
    hiddenFields: PropTypes.array,
  };

  constructor(props) {
    super(props);
    this.state = {
      readerToDelete: {},
    };
    this.sortingManager = new SortingManager();
  }

  render() {
    const {recordPages, onSortableFieldClick, hiddenFields} = this.props;
    const readers = recordPages[recordPages.currentPage] || {};
    const records = buildClassroomReaderRecords(readers);
    const settings = buildClassroomReaderSettings();
    return (
      <KluwellTableCore
        {...{
          records,
          settings,
          sortingManager: this.sortingManager,
          onSortableFieldClick,
          hiddenFields,
        }}
      />
    );
  }
}

export default withKluwellTableWrapper(ClassroomReaderTableCore);
